import React, { useState, useMemo } from 'react';
import { Wind, PoundSterling, Calendar, Users, TrendingUp, Zap, Leaf, Briefcase, Building2, Home, Landmark, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Slider } from './ui/slider';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { calculateMetrics, formatCurrency, formatNumber, formatCO2, formatPower } from '../utils/calculations';
import modelConfig from '../data/modelConfig.json';
import DevexPhasingChart from './charts/DevexPhasingChart';
import FundingFlowChart from './charts/FundingFlowChart';
import CommunityValueChart from './charts/CommunityValueChart';
import DataTable from './DataTable';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const Dashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('dashboard_auth') === 'true';
  });
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const [turbineCount, setTurbineCount] = useState(100);
  const [inputValue, setInputValue] = useState('100');
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    const validCodes = ['enercon', 'rotor2025', 'pitch2025', 'turbine2025', 'vortex2025', 'nacelle2025', 'tower2025', 'hub2025', 'energy2025', 'project2025'];
    const enteredCode = password.toLowerCase();
    
    if (validCodes.includes(enteredCode)) {
      // Generate unique session ID
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Log the access attempt to backend with enhanced data
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/log-access`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: enteredCode,
            timestamp: new Date().toISOString(),
            session_id: sessionId
          })
        });
        
        // Store session start time and ID
        sessionStorage.setItem('session_id', sessionId);
        sessionStorage.setItem('session_start', Date.now().toString());
      } catch (error) {
        console.log('Logging failed, but allowing access');
      }
      
      sessionStorage.setItem('dashboard_auth', 'true');
      sessionStorage.setItem('access_code', enteredCode);
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
      setPassword('');
    }
  };
  
  // Slide URLs
  const slides = [
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/zry9sxun_Slide1.PNG',
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/vudd16ut_Slide2.PNG',
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/xivkes6o_Slide3.PNG',
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/pztfsnb8_Slide4.PNG',
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/f2dgtgxj_Slide5.PNG',
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/4tcpgeqe_Slide6.PNG',
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/6lpfyyyi_Slide7.PNG',
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/gp69nz4y_Slide8.PNG',
    'https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/ynufqsjm_Slide9.PNG'
  ];
  
  const openSlideshow = () => {
    setCurrentSlide(0);
    setShowSlideshow(true);
  };
  
  const closeSlideshow = () => {
    setShowSlideshow(false);
  };
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };
  
  // Track session duration
  React.useEffect(() => {
    if (!isAuthenticated) return;
    
    const updateSession = async () => {
      const sessionId = sessionStorage.getItem('session_id');
      const sessionStart = sessionStorage.getItem('session_start');
      
      if (sessionId && sessionStart) {
        const duration = Math.floor((Date.now() - parseInt(sessionStart)) / 1000); // in seconds
        
        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/update-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id: sessionId,
              duration: duration,
              last_activity: new Date().toISOString()
            })
          });
        } catch (error) {
          console.log('Session update failed');
        }
      }
    };
    
    // Update session every 30 seconds
    const interval = setInterval(updateSession, 30000);
    
    // Update on page unload
    window.addEventListener('beforeunload', updateSession);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', updateSession);
      updateSession(); // Final update
    };
  }, [isAuthenticated]);

  const metrics = useMemo(() => {
    return calculateMetrics(turbineCount, modelConfig);
  }, [turbineCount]);

  const handleSliderChange = (value) => {
    const newValue = value[0];
    setTurbineCount(newValue);
    setInputValue(newValue.toString());
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 125) {
      setTurbineCount(numValue);
    }
  };

  const handleInputBlur = () => {
    // Sync input with turbineCount on blur in case of invalid input
    setInputValue(turbineCount.toString());
  };

  const handleReset = () => {
    setTurbineCount(30);
    setInputValue('30');
  };

  const [isExporting, setIsExporting] = React.useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Scroll to top before capturing
      window.scrollTo(0, 0);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create PDF in A4 portrait format
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 8;
      const contentWidth = pageWidth - (2 * margin);
      
      // Helper function to capture and add section
      const captureSection = async (elementId, title) => {
        const element = document.getElementById(elementId);
        if (!element) return null;
        
        const canvas = await html2canvas(element, {
          scale: 1.8,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true
        });
        
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        return { imgData, imgWidth, imgHeight, title };
      };
      
      // Page 1: Header + KPIs
      let currentPage = 1;
      
      // Add CPS logo (left side)
      try {
        const logoImg = new Image();
        logoImg.src = '/cps-logo.png';
        await new Promise((resolve) => {
          logoImg.onload = resolve;
          logoImg.onerror = resolve; // Continue even if logo fails
        });
        if (logoImg.complete) {
          pdf.addImage(logoImg, 'PNG', margin, 8, 25, 12);
        }
      } catch (e) {
        console.log('Logo load failed, continuing without it');
      }
      
      // Add title (center-right)
      pdf.setFontSize(16);
      pdf.setTextColor(46, 125, 50);
      pdf.text('GB Energy Community Wind Programme', pageWidth / 2 + 10, 12, { align: 'center' });
      pdf.setFontSize(11);
      pdf.setTextColor(44, 62, 80);
      pdf.text(`${turbineCount} Turbines Analysis`, pageWidth / 2 + 10, 18, { align: 'center' });
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      pdf.text(new Date().toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      }), pageWidth - margin, 18, { align: 'right' });
      
      // Separator line
      pdf.setDrawColor(165, 214, 167);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 22, pageWidth - margin, 22);
      
      let yPosition = 26;
      
      // Capture KPIs
      const kpiSection = await captureSection('kpi-section', 'Key Metrics');
      if (kpiSection) {
        const scaledHeight = Math.min(kpiSection.imgHeight, 35);
        pdf.addImage(kpiSection.imgData, 'PNG', margin, yPosition, kpiSection.imgWidth, scaledHeight);
        yPosition += scaledHeight + 4;
      }
      
      // Capture Portfolio Impact
      const portfolioSection = await captureSection('portfolio-section', 'Portfolio Impact');
      if (portfolioSection) {
        const scaledHeight = Math.min(portfolioSection.imgHeight, 45);
        const remainingSpace = pageHeight - yPosition - margin - 10;
        
        if (scaledHeight > remainingSpace) {
          pdf.addPage();
          currentPage++;
          yPosition = margin + 5;
        }
        
        pdf.addImage(portfolioSection.imgData, 'PNG', margin, yPosition, portfolioSection.imgWidth, scaledHeight);
        yPosition += scaledHeight + 4;
      }
      
      // Charts (DEVEX + Funding) - try to fit on same page
      const chartsSection = await captureSection('charts-section', 'Charts');
      if (chartsSection) {
        const scaledHeight = Math.min(chartsSection.imgHeight, 90);
        const remainingSpace = pageHeight - yPosition - margin - 10;
        
        if (scaledHeight > remainingSpace) {
          pdf.addPage();
          currentPage++;
          yPosition = margin + 5;
        }
        
        pdf.addImage(chartsSection.imgData, 'PNG', margin, yPosition, chartsSection.imgWidth, scaledHeight);
        yPosition += scaledHeight + 4;
      }
      
      // Community Value Chart - new page
      pdf.addPage();
      currentPage++;
      yPosition = margin + 5;
      
      const communitySection = await captureSection('community-section', 'Community Value');
      if (communitySection) {
        const scaledHeight = Math.min(communitySection.imgHeight, 180);
        pdf.addImage(communitySection.imgData, 'PNG', margin, yPosition, communitySection.imgWidth, scaledHeight);
        yPosition += scaledHeight + 4;
      }
      
      // Detailed Data - try to continue on current page if space available
      const detailedDataSection = await captureSection('detailed-data-section', 'Detailed Data');
      if (detailedDataSection) {
        const remainingSpace = pageHeight - yPosition - margin - 10;
        
        if (remainingSpace < 50) {
          pdf.addPage();
          currentPage++;
          yPosition = margin + 5;
        }
        
        let remainingHeight = detailedDataSection.imgHeight;
        let sourceY = 0;
        let targetY = yPosition;
        
        while (remainingHeight > 0) {
          const availableHeight = pageHeight - targetY - margin;
          const heightToDraw = Math.min(remainingHeight, availableHeight);
          
          // Create a temporary canvas for the slice
          const sliceCanvas = document.createElement('canvas');
          const ctx = sliceCanvas.getContext('2d');
          const img = new Image();
          img.src = detailedDataSection.imgData;
          
          await new Promise((resolve) => {
            img.onload = () => {
              const scale = 2;
              sliceCanvas.width = img.width;
              sliceCanvas.height = (heightToDraw / detailedDataSection.imgHeight) * img.height;
              
              ctx.drawImage(
                img,
                0, (sourceY / detailedDataSection.imgHeight) * img.height,
                img.width, sliceCanvas.height,
                0, 0,
                sliceCanvas.width, sliceCanvas.height
              );
              resolve();
            };
          });
          
          const sliceData = sliceCanvas.toDataURL('image/png');
          pdf.addImage(sliceData, 'PNG', margin, targetY, contentWidth, heightToDraw);
          
          remainingHeight -= heightToDraw;
          sourceY += heightToDraw;
          
          if (remainingHeight > 0) {
            pdf.addPage();
            currentPage++;
            targetY = margin + 5;
          }
        }
      }
      
      // Add page numbers
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
      }
      
      pdf.save(`GB-Energy-Dashboard-${turbineCount}-Turbines.pdf`);
      
      // Success notification
      alert(`✅ Dashboard exported successfully!\n\n${totalPages} pages saved: GB-Energy-Dashboard-${turbineCount}-Turbines.pdf`);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('❌ Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const kpiCards = [
    {
      title: 'Turbines',
      subtitle: 'Programme size',
      value: turbineCount,
      formatter: (v) => formatNumber(v),
      icon: Wind,
      color: 'text-emerald-700'
    },
    {
      title: 'Total DEVEX',
      subtitle: 'With economies',
      value: metrics.totalDevex,
      formatter: (v) => formatCurrency(v, 1),
      icon: PoundSterling,
      color: 'text-emerald-700'
    },
    {
      title: 'Development Cost',
      subtitle: 'Per Turbine',
      value: metrics.unitDevex,
      formatter: (v) => formatCurrency(v, 0),
      icon: TrendingUp,
      color: 'text-emerald-700'
    },
    {
      title: 'Total Benefit',
      subtitle: '30-year (Year 30)',
      value: metrics.totalBenefitYear30,
      formatter: (v) => formatCurrency(v, 1),
      icon: Users,
      color: 'text-emerald-700'
    },
    {
      title: 'Programme Duration',
      subtitle: 'Tranche-driven',
      value: metrics.programmeDuration,
      formatter: (v) => `${v} years`,
      icon: Calendar,
      color: 'text-emerald-700'
    }
  ];

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f0f9f4' }}>
        <Card className="w-full max-w-md shadow-2xl border-emerald-300">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-200">
            <div className="flex flex-col items-center gap-4 py-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_wind-dashboard-1/artifacts/hvp9krnc_CPS_Wide_Trans.png" 
                alt="Community Power Solutions"
                className="h-16"
                style={{ objectFit: 'contain' }}
              />
              <CardTitle className="text-2xl text-center" style={{ color: '#1B5E20', fontFamily: 'Montserrat, sans-serif' }}>
                GB Energy Community Wind Dashboard
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-8 pb-8 bg-white">
            <form onSubmit={handleLogin}>
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                    🔒 This dashboard contains confidential information
                  </p>
                  <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Please enter the password to access
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Password
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full text-base"
                    autoFocus
                  />
                  {passwordError && (
                    <p className="text-red-600 text-sm mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {passwordError}
                    </p>
                  )}
                </div>
                
                <Button 
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3"
                >
                  Enter Dashboard
                </Button>
                
                <div className="text-center mt-6">
                  <p className="text-xs text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
                    © 2025 Community Power Solutions
                  </p>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f0f9f4' }}>
      {/* Confidentiality Banner */}
      <div className="w-full py-2 text-center text-sm font-semibold" style={{ backgroundColor: '#FFF3CD', color: '#856404', borderBottom: '1px solid #FFC107' }}>
        🔒 CONFIDENTIAL - For GBE/CPS Discussion Only - Not for Distribution
      </div>
      
      {/* Header */}
      <header className="border-b-4" style={{ backgroundColor: '#A5D6A7', borderColor: '#81C784' }}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_wind-dashboard-1/artifacts/hvp9krnc_CPS_Wide_Trans.png" 
                alt="Community Power Solutions"
                className="h-20"
                style={{ objectFit: 'contain' }}
              />
              <div className="border-l-2 border-green-700 pl-6">
                <h1 className="text-2xl font-bold" style={{ color: '#1B5E20', fontFamily: 'Inter, sans-serif' }}>
                  GB Energy — Community Wind Portfolio Overview
                </h1>
                <p className="text-green-800 mt-1 text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Development Programme Calculator
                </p>
              </div>
            </div>
            <Button 
              onClick={handleExport}
              disabled={isExporting}
              className="bg-white hover:bg-green-50 font-semibold shadow-md disabled:opacity-50"
              style={{ color: '#2E7D32' }}
            >
              {isExporting ? '⏳ Exporting...' : '📄 Export Snapshot'}
            </Button>
          </div>
        </div>
      </header>

      <div id="dashboard-content" className="container mx-auto px-6 py-8">
        {/* Input Control */}
        <Card className="mb-8 shadow-lg border-emerald-200">
          <CardContent className="pt-6 pb-4 bg-white">
            {/* Introduction Text */}
            <p className="text-xl text-gray-700 mb-4" style={{ fontFamily: 'Lato, sans-serif', lineHeight: '1.6' }}>
              This dashboard outlines our proposal for a fleet of community-owned onshore wind turbines. You can adjust the scale of the fleet using the slider below and immediately see the projected investment, clean power output and community benefits associated. It has been provided to support ongoing discussions.
            </p>
            
            {/* Compact Slider Section */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Number of Turbines
                </label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    min={1}
                    max={125}
                    className="w-20 h-9 text-center text-base font-semibold"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  />
                  <Button 
                    onClick={handleReset} 
                    variant="outline"
                    size="sm"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-9"
                  >
                    Reset
                  </Button>
                </div>
              </div>
              <Slider
                value={[turbineCount]}
                onValueChange={handleSliderChange}
                min={1}
                max={125}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
                <span>125</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div id="kpi-section" className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
          {kpiCards.map((kpi, index) => {
            const Icon = kpi.icon;
            
            // Define background colors for each KPI card (matching Carbon Emissions chart boxes)
            const cardBackgrounds = [
              'bg-blue-50',      // Turbines - Blue (matching Clean Energy Generated box)
              'bg-emerald-50',   // Total DEVEX - Green (matching Total CO₂ Avoided box)
              'bg-blue-50',      // Development Cost - Blue (matching Clean Energy Generated box)
              'bg-emerald-50',   // Total Community Benefit - Green (matching Total CO₂ Avoided box)
              'bg-blue-50'       // Programme Duration - Blue (matching Clean Energy Generated box)
            ];
            
            const borderColors = [
              'border-blue-200',     // Blue border
              'border-emerald-200',  // Green border
              'border-blue-200',     // Blue border
              'border-emerald-200',  // Green border
              'border-blue-200'      // Blue border
            ];
            
            // Colors matching the Carbon Emissions chart impact boxes
            const numberColors = [
              '#1565C0',  // Dark blue for Turbines (darker version of bright blue)
              '#48A46A',  // Medium green for Total DEVEX (matching Total CO₂ Avoided)
              '#1565C0',  // Dark blue for Development Cost (darker version of bright blue)
              '#48A46A',  // Medium green for Total Benefit (matching Total CO₂ Avoided)
              '#1565C0'   // Dark blue for Programme Duration (darker version of bright blue)
            ];
            
            return (
              <Card key={index} className={`shadow-lg hover:shadow-xl transition-shadow ${borderColors[index]} overflow-hidden`}>
                <CardContent className={`pt-6 pb-6 ${cardBackgrounds[index]}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-base font-bold text-gray-700 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                        {kpi.title}
                      </p>
                      {kpi.subtitle && (
                        <p className="text-xs text-gray-500 mb-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                          {kpi.subtitle}
                        </p>
                      )}
                      <p className="text-3xl font-bold" style={{ color: numberColors[index], fontFamily: 'Montserrat, sans-serif' }}>
                        {kpi.formatter(kpi.value)}
                      </p>
                    </div>
                    <Icon className={`w-8 h-8 ${kpi.color}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Impact Strip */}
        <Card id="portfolio-section" className="shadow-lg border-emerald-200 mb-8">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-100">
            <CardTitle className="text-lg" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
              Portfolio Impact Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              <div className="text-center">
                <Zap className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                  {formatPower(metrics.annualGenerationMWh)}
                </p>
                <p className="text-xs text-gray-600 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Power/year
                </p>
              </div>
              <div className="text-center">
                <Leaf className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                  {formatCO2(metrics.annualCO2Tonnes)}
                </p>
                <p className="text-xs text-gray-600 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  CO\u2082 avoided/year
                </p>
              </div>
              <div className="text-center">
                <Briefcase className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                  {formatNumber(metrics.jobsFTE, 1)}
                </p>
                <p className="text-xs text-gray-600 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Jobs (FTE)
                </p>
              </div>
              <div className="text-center">
                <Building2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                  {formatCurrency(metrics.leveragedCapital, 1)}
                </p>
                <p className="text-xs text-gray-600 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Leveraged capital
                </p>
              </div>
              <div className="text-center">
                <Landmark className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                  {formatCurrency(metrics.lifetimeBusinessRates, 1)}
                </p>
                <p className="text-xs text-gray-600 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Business rates (25yr)
                </p>
              </div>
              <div className="text-center">
                <Home className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                  {formatCurrency(metrics.lifetimeLandRent, 1)}
                </p>
                <p className="text-xs text-gray-600 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Land rent (25yr)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charts Section */}
        <div id="charts-section" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="shadow-lg border-emerald-200">
            <CardHeader className="bg-white border-b border-emerald-100">
              <CardTitle className="text-lg" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                DEVEX Phasing (10-Year View)
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                Annual development investment as turbines are deployed in tranches across the programme
              </p>
            </CardHeader>
            <CardContent className="pt-4 bg-white">
              <DevexPhasingChart data={metrics.annualDevexSpend} />
              
              {/* Compact Turbine Selector */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Quick Adjust Turbine Numbers
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={125}
                    value={inputValue}
                    onChange={handleInputChange}
                    className="w-16 h-8 text-center text-sm"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  />
                </div>
                <Slider
                  value={[turbineCount]}
                  onValueChange={handleSliderChange}
                  min={1}
                  max={125}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1</span>
                  <span>25</span>
                  <span>50</span>
                  <span>75</span>
                  <span>100</span>
                  <span>125</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-emerald-200">
            <CardHeader className="bg-white border-b border-emerald-100">
              <CardTitle className="text-lg" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                GB Energy Funding Flow — Outlay vs Recycle
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                Demonstrating circular funding: capital deployed, projects built, funds recycled for next wave
              </p>
            </CardHeader>
            <CardContent className="pt-6 bg-white">
              <FundingFlowChart phasingData={metrics.phasingTable} />
            </CardContent>
          </Card>
        </div>

        <Card id="community-section" className="shadow-lg border-emerald-200 mb-8">
          <CardHeader className="bg-white border-b border-emerald-100">
            <CardTitle className="text-lg" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
              Community Value Generated — 30-Year Lifecycle
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
              Shows how GB Energy's early development investment enables leveraged capital and delivers 30 years of community value through funds, land rent, and business rates. Figures index linked at 2% per annum.
            </p>
          </CardHeader>
          <CardContent className="pt-6 bg-white">
            <CommunityValueChart metrics={metrics} />
          </CardContent>
        </Card>

        {/* Detailed Data Table */}
        <div id="detailed-data-section">
          <DataTable 
            metrics={metrics} 
            turbineCount={turbineCount}
            inputValue={inputValue}
            handleSliderChange={handleSliderChange}
            handleInputChange={handleInputChange}
            openSlideshow={openSlideshow}
          />
        </div>
      </div>
      
      {/* Slideshow Modal */}
      {showSlideshow && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={closeSlideshow}
        >
          <div 
            className="relative max-w-6xl max-h-[90vh] w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeSlideshow}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-10 h-10" />
            </button>
            
            {/* Slide Counter */}
            <div className="absolute -top-12 left-0 text-white text-lg font-semibold">
              Slide {currentSlide + 1} of {slides.length}
            </div>
            
            {/* Slide Image */}
            <img 
              src={slides[currentSlide]} 
              alt={`Slide ${currentSlide + 1}`}
              className="w-full h-auto rounded-lg shadow-2xl"
              style={{ maxHeight: '85vh', objectFit: 'contain' }}
            />
            
            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-3 transition-all shadow-lg"
            >
              <ChevronLeft className="w-8 h-8 text-gray-800" />
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-3 transition-all shadow-lg"
            >
              <ChevronRight className="w-8 h-8 text-gray-800" />
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t-2 border-emerald-300 mt-12" style={{ backgroundColor: '#C8E6C9' }}>
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_wind-dashboard-1/artifacts/hvp9krnc_CPS_Wide_Trans.png" 
                alt="Community Power Solutions"
                className="h-12"
                style={{ objectFit: 'contain' }}
              />
              <div className="border-l-2 border-green-600 pl-4">
                <p className="text-sm font-medium" style={{ color: '#1B5E20', fontFamily: 'Lato, sans-serif' }}>
                  Data source: CPS Wind Portfolio Calculator — Nov 2025
                </p>
                <a 
                  href="https://www.communitypowersolutions.org" 
                  className="text-sm hover:underline"
                  style={{ color: '#2E7D32', fontFamily: 'Lato, sans-serif' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.communitypowersolutions.org
                </a>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs" style={{ color: '#1B5E20', fontFamily: 'Lato, sans-serif' }}>
                © 2025 Community Power Solutions
              </p>
              <p className="text-xs mt-1" style={{ color: '#2E7D32', fontFamily: 'Lato, sans-serif' }}>
                All rights reserved
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
